import React, { useState, useEffect } from 'react';
import { PANE_WIDE_WIDTH } from '../constants/paneLayout';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  FlatList,
  Image,
  Platform,
  RefreshControl,
  ActivityIndicator,
  TextInput,
  useWindowDimensions,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import {
  Button,
  Logo,
  Header,
  BottomTabs,
  ScreenLayout,
  MoreOptionsModal,
  ProfileCompletionWidget,
  CampaignDetailsModal,
  ContactEditForm,
  getChannelName,
  colors,
  spacing,
  typography
} from '../components';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useLocale } from '../contexts/LocaleContext';
import { useAppStore } from '../store/appStore';
import { Ionicons } from '@expo/vector-icons';
import { apiService } from '../services/api';
import { ServiceStatus } from '../components/ProfileCompletionWidget';
import { HomeDashboard } from '../components/home/HomeDashboard';
import type { RecentPost } from './dashboard/RecentPostsCard';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { useSetupProgress } from '../hooks/useSetupProgress';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useIsMobileBrowser } from '../hooks/useIsMobileBrowser';
import { useDesktopPaneLayout } from '../hooks/useDesktopPaneLayout';
import { usePaneShellConfig } from '../components/PersistentPaneShell';
import { ConversationScreen } from './ConversationScreen';
import { CallHistoryScreen } from './CallHistoryScreen';
import { PaneHeader, PaneHeaderIconButton } from '../components/PaneHeader';
import { ConversationIdentifierDropdown } from '../components/ConversationIdentifierDropdown';
import { useContactConversationPane } from '../hooks/useContactConversationPane';
import { usePermissions } from '../hooks/usePermissions';
import { notificationService } from '../services/notification.service';

// Responsive card sizing — takes the CURRENT window width/mobile-browser
// flag as parameters (from useWindowDimensions()/useIsMobileBrowser() inside
// the component) rather than reading a module-scope Dimensions.get()
// snapshot, which was captured once at module load and never updated on
// device rotation or browser window resize.
const getCardWidth = (type: 'platform' | 'action' | 'campaign', screenWidth: number, isMobileBrowser: boolean) => {
  if (isMobileBrowser) {
    // Mobile browser gets mobile app layout with better sizing
    switch (type) {
      case 'platform': return screenWidth * 0.4;
      case 'action': return Math.max(110, screenWidth * 0.28); // Larger width for comfortable mobile browser experience
      case 'campaign': return screenWidth * 0.7;
      default: return screenWidth * 0.4;
    }
  } else if (Platform.OS === 'web') {
    // Desktop web gets optimized layout
    switch (type) {
      case 'platform': return Math.min(320, screenWidth * 0.22);
      case 'action': return Math.min(140, screenWidth * 0.08); // Smaller width for 8 actions in 1-line layout
      case 'campaign': return Math.min(400, screenWidth * 0.28);
      default: return 320;
    }
  } else {
    // Native mobile app - use larger width for better mobile experience
    switch (type) {
      case 'platform': return screenWidth * 0.4;
      case 'action': return Math.max(110, screenWidth * 0.28); // Larger width for comfortable mobile experience (110px minimum or 28% of screen)
      case 'campaign': return screenWidth - (spacing.md * 4); // Full width minus padding for one card per screen
      default: return screenWidth * 0.4;
    }
  }
};

interface HomeScreenProps {
  /** False when this screen is mounted inside PersistentPaneShell but
   * currently hidden (another screen in the group is active) — see
   * usePaneShellConfig's own doc comment for why this must gate shell
   * registration. Always true outside the shell (native/narrow mobile-web). */
  isActive?: boolean;
  /** A contact id read from the URL's `?contact=` query param at app boot —
   * restores that contact's profile+conversation on page load/refresh. See
   * useContactConversationPane's openContactById. */
  deepLinkContactId?: string | null;
  /** Called once the deep-linked contact is open, so later visits start on the screen itself. */
  onDeepLinkContactOpened?: () => void;
}

export const HomeScreen: React.FC<HomeScreenProps> = ({ isActive = true, deepLinkContactId, onDeepLinkContactOpened }) => {
  const { user, tokens, logout, businessRole, currentBusiness, currentSubscription, loadCurrentSubscription } = useAppStore();
  
  // Extract subscription data from user object if available (from API response)
  const userSubscription = (user as any)?.subscription;
  const { handleLogout: onLogout, showNotification, activeTab, setActiveTab, navigateTo, startPhoneCall, isPhoneDialerReady, routeParams } = useAppContext();
  const requestedContactId = routeParams?.assistantContactId || deepLinkContactId;
  const { t } = useLocale();
  const isMobileBrowser = useIsMobileBrowser();
  // Shared with AppNavigator (which routes 'home' through PersistentPaneShell
  // on desktop web) and the other shell screens — must stay the one shared
  // hook so the two sides can never disagree about which path is active.
  const isDesktopPaneLayout = useDesktopPaneLayout();
  // Reactive to rotation/window resize — replaces the old module-scope
  // Dimensions.get('window') snapshot, which never updated after first load.
  const { width: screenWidth } = useWindowDimensions();
  const [showRightPane, setShowRightPane] = useState(true); // Always show right pane
  const [showLeftPane] = useState(true); // Settings pane on the left
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  // 'contacts' removed — that list now lives in the left pane (leftPaneContent="contacts")
  // instead of being toggled into this pane. 'view-conversation' added
  // alongside the existing 'add-contact' — same right-pane slot, driven by
  // clicking a contact in the left pane (see useContactConversationPane).
  const [rightPaneContent, setRightPaneContent] = useState<'add-contact' | 'view-conversation' | 'view-call-history' | 'empty'>('empty');
  // The clicked contact's profile (shown in the middle, replacing the
  // dashboard) and conversation/call-history (shown in the right pane) —
  // see useContactConversationPane's own doc comment.
  const { viewedContact, conversationId: viewedConversationId, selectedChannel: conversationSelectedChannel, selectedIdentifierValue: conversationSelectedIdentifierValue, identifierOptions: conversationIdentifierOptions, switchConversationIdentifier, openContact: openContactConversation, openContactById, closeContact: closeContactConversation, callHistoryTarget, openCallHistory, closeCallHistory } = useContactConversationPane();

  const handleCallHistoryCall = async () => {
    if (!callHistoryTarget || !isPhoneDialerReady) return;
    let target = callHistoryTarget.phoneNumber;
    try {
      const res = await apiService.getContactDialTarget(callHistoryTarget.contactId, undefined, undefined, undefined, callHistoryTarget.phoneIndex);
      target = res.data?.phoneNumber || target;
    } catch (error) {
      console.error('[HomeScreen] Failed to resolve phone call target', error);
    }
    if (!target) return;
    startPhoneCall(target, callHistoryTarget.contactId, { name: callHistoryTarget.contactName || target });
  };

  const handleContactRowPress = (contact: any) => {
    setActiveTab('contacts');
    openContactConversation(contact);
    setRightPaneContent('view-conversation');
  };

  const closeViewedContact = () => {
    closeContactConversation();
    setRightPaneContent('empty');
  };

  const closeViewedCallHistory = () => {
    closeCallHistory();
    setRightPaneContent('empty');
  };

  const navigateAwayFromHome = (screen: Parameters<typeof navigateTo>[0], param?: string, routeParams?: Record<string, any>) => {
    closeViewedContact();
    navigateTo(screen, param, routeParams);
  };

  useEffect(() => {
    if (!isActive) {
      closeViewedContact();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Restore a `?contact=<id>` deep link on page load/refresh — guarded on
  // !viewedContact so it never clobbers a contact the user already opened
  // (or changed) by clicking, and in practice only ever fires once.
  useEffect(() => {
    if (requestedContactId && (!viewedContact || routeParams?.assistantContactId && requestedContactId !== viewedContact.id)) {
      openContactById(requestedContactId);
      setRightPaneContent('view-conversation');
    }
    if (deepLinkContactId) onDeepLinkContactOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedContactId, routeParams?.assistantNavigationKey]);
  const [latestCampaigns, setLatestCampaigns] = useState<any[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [systemAlerts, setSystemAlerts] = useState<any[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  
  // Load subscription data on component mount if not already loaded
  useEffect(() => {
    console.log('🔍 [HomeScreen] useEffect check:', {
      hasCurrentBusiness: !!currentBusiness,
      hasCurrentSubscription: !!currentSubscription,
      businessSubscriptionPlan: currentBusiness?.subscriptionPlan,
      shouldLoad: currentBusiness && !currentSubscription
    });
    
    if (currentBusiness && !currentSubscription) {
      console.log('🔍 [HomeScreen] Loading subscription data...');
      loadCurrentSubscription();
    } else if (!currentBusiness) {
      console.log('⚠️ [HomeScreen] No current business available for subscription loading');
    } else if (currentSubscription) {
      console.log('✅ [HomeScreen] Subscription already loaded');
    }
  }, [currentBusiness, currentSubscription, loadCurrentSubscription]);

  // Fetch latest campaigns
  const fetchLatestCampaigns = async () => {
    if (!currentBusiness?._id) {
      return;
    }

    setCampaignsLoading(true);
    try {
      const response = await apiService.authenticatedRequest<any>(
        `/api/omni-channel/campaigns/latest?limit=10`
      );

      if (response.statusCode === 1000 && response.data) {
        // Map API response to match campaign card structure
        const mappedCampaigns = response.data.map((campaign: any) => ({
          id: campaign.id || campaign.taskId,
          taskId: campaign.taskId || campaign.id, // CRITICAL: Include taskId for loading details
          name: campaign.name,
          platform: campaign.platform,
          status: campaign.status,
          progress: campaign.progress,
          sent: campaign.sent || 0,
          delivered: campaign.delivered || 0,
          opened: campaign.opened || 0,
          read: campaign.read || 0, // Add read field
          contactReplied: campaign.contactReplied || 0,
          handledConversations: campaign.handledConversations || 0,
          endDate: campaign.endDate,
          nextExecutionAt: campaign.nextExecutionAt,
          lastExecutedAt: campaign.lastExecutedAt,
          createdAt: campaign.createdAt,
          budget: '$0', // Placeholder - can be enhanced later
          spent: '$0', // Placeholder - can be enhanced later
          totalContacts: campaign.totalContacts || 0,
          successful: campaign.successful || 0,
          failed: campaign.failed || 0,
        }));
        setLatestCampaigns(mappedCampaigns);
      } else {
        console.warn('⚠️ [HomeScreen] Failed to fetch campaigns:', response.messages || response.errors);
        setLatestCampaigns([]);
      }
    } catch (error: any) {
      console.error('❌ [HomeScreen] Error fetching campaigns:', error);
      setLatestCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  };

  // Load campaigns when business is available
  useEffect(() => {
    if (currentBusiness?._id) {
      fetchLatestCampaigns();
      fetchSystemAlerts();
    }
  }, [currentBusiness?._id]);

  // Fetch system alerts
  const fetchSystemAlerts = async () => {
    if (!currentBusiness?._id) {
      return;
    }

    setAlertsLoading(true);
    try {
      const response = await apiService.getNotifications({
        limit: 10,
        types: ['system'],
        unreadOnly: false,
      });

      if (response.statusCode === 1000 && response.data?.notifications) {
        setSystemAlerts(response.data.notifications);
      } else {
        console.warn('⚠️ [HomeScreen] Failed to fetch system alerts:', response.messages || response.errors);
        setSystemAlerts([]);
      }
    } catch (error: any) {
      console.error('❌ [HomeScreen] Error fetching system alerts:', error);
      setSystemAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  };

  // Helper functions for task details
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getExecutionStatusColor = (status: string) => {
    if (status === 'success') return colors.success || '#10B981';
    if (status === 'failed') return colors.error || '#EF4444';
    return colors.warning || '#F59E0B';
  };

  const renderExecutionLog = ({ item: log }: { item: any }) => {
    const statusColor = getExecutionStatusColor(log.status);
    const duration = log.duration ? `${(log.duration / 1000).toFixed(2)}s` : 'N/A';

    return (
      <View style={styles.executionLogCard}>
        <View style={styles.executionLogHeader}>
          <View style={[styles.executionStatusDot, { backgroundColor: statusColor }]} />
          <Text style={styles.executionStatusText}>
            {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
          </Text>
          {log.retryAttempt > 0 && (
            <Text style={styles.retryText}>
              (Retry #{log.retryAttempt})
            </Text>
          )}
        </View>

        <View style={styles.executionLogDetails}>
          <View style={styles.executionLogRow}>
            <Text style={styles.executionLogLabel}>Started:</Text>
            <Text style={styles.executionLogValue}>{formatDate(log.startedAt)}</Text>
          </View>
          {log.completedAt && (
            <View style={styles.executionLogRow}>
              <Text style={styles.executionLogLabel}>Completed:</Text>
              <Text style={styles.executionLogValue}>{formatDate(log.completedAt)}</Text>
            </View>
          )}
          <View style={styles.executionLogRow}>
            <Text style={styles.executionLogLabel}>Duration:</Text>
            <Text style={styles.executionLogValue}>{duration}</Text>
          </View>
          {log.error && (
            <View style={styles.errorContainer}>
              <Text style={styles.errorLabel}>Error:</Text>
              <Text style={styles.errorText}>{log.error}</Text>
            </View>
          )}
        </View>
      </View>
    );
  };

  const handleCampaignPress = (campaign: any) => {
    setSelectedCampaign(campaign);
    setShowCampaignModal(true);
  };

  // Debug business role and subscription data on every render
  console.log('🔍 [HomeScreen] Business role check:', {
    businessRole,
    user: user ? { id: user.id, email: user.email } : null,
    currentBusiness: currentBusiness ? { 
      id: currentBusiness._id, 
      name: currentBusiness.name, 
      subscriptionPlan: currentBusiness.subscriptionPlan,
      subscriptionStatus: currentBusiness.subscriptionStatus,
      maxChannels: currentBusiness.maxChannels,
      maxUsers: currentBusiness.maxUsers
    } : null,
    currentSubscription: currentSubscription ? { 
      planCode: currentSubscription.planCode, 
      hasServices: !!currentSubscription.services,
      services: currentSubscription.services ? Object.keys(currentSubscription.services) : []
    } : null,
    isOwner: businessRole === 'owner'
  });
  const insets = useSafeAreaInsets();
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [channelsData, setChannelsData] = useState<any>(null);
  const [homeRefreshToken, setHomeRefreshToken] = useState(0);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [recentPosts, setRecentPosts] = useState<RecentPost[]>([]);
  // Owners/admins: "Get your business ready" checklist (its own screen).
  // Hiding the banner is remembered on this device, per business.
  const setup = useSetupProgress('home');
  const setupHiddenKey = `dislix.setupHidden.${currentBusiness?._id || 'none'}`;
  const [setupHidden, setSetupHidden] = useState(false);
  useEffect(() => {
    let active = true;
    AsyncStorage.getItem(setupHiddenKey)
      .then((value) => { if (active) setSetupHidden(value === '1'); })
      .catch(() => { if (active) setSetupHidden(false); });
    return () => { active = false; };
  }, [setupHiddenKey]);
  const hideSetup = () => {
    setSetupHidden(true);
    AsyncStorage.setItem(setupHiddenKey, '1').catch(() => {});
  };
  // Use centralized profile completion hook
  const { profileCompletion, whatsappPhones, facebookPages, xAccounts, phoneAccount, businessStats, isLoading: profileLoading, error: profileError, refresh: refreshProfile } = useProfileCompletion();
  const { hasPermissionAtLeast } = usePermissions();
  console.log('🔍 [SocialHubScreen] width size:', isMobileBrowser ? spacing.screenPadding : (Platform.OS === 'web' ? Math.min(screenWidth * 0.1, 120) : spacing.screenPadding));

  // Create styles with dynamic insets
  const styles = StyleSheet.create({
    content: {
      flex: 1,
      marginTop: 20,
      //paddingHorizontal: isMobileBrowser ? 10 : (Platform.OS === 'web' ? Math.min(screenWidth * 0.1, 120) : spacing.screenPadding),
      paddingBottom: Platform.OS === 'web' ? 0 : 100, // No padding on web, large padding on mobile for bottom tabs
      maxWidth: isMobileBrowser ? undefined : (Platform.OS === 'web' ? 1400 : undefined),
      alignSelf: isMobileBrowser ? undefined : (Platform.OS === 'web' ? 'center' : undefined),
      width: isMobileBrowser ? undefined : (Platform.OS === 'web' ? '100%' : undefined),
    },
    contentContainerWeb: {
      paddingBottom: spacing.sm, // Minimal padding on web
    },
    section: {
      marginBottom: Platform.OS === 'web' ? spacing.lg : spacing.xl, // Less spacing on web
    },
    // Profile Completion + Quick Actions were always forced side-by-side
    // (hardcoded flexDirection: 'row', no platform/width check at all) — on
    // native or a narrow mobile-web viewport this squeezed both into a
    // cramped row instead of stacking. Native always stacks; web stacks
    // below the mobile-browser threshold, matching the other Home-screen
    // analytics sections (PlatformPerformanceCard, DashboardAnalyticsSection).
    topSectionRow: {
      flexDirection: Platform.OS !== 'web' || isMobileBrowser ? 'column' : 'row',
      alignItems: 'stretch',
      width: '100%',
      gap: spacing.sm,
      marginBottom: Platform.OS === 'web' ? spacing.lg : spacing.xl,
    },
    profileCompletionColumn: {
      flex: Platform.OS !== 'web' || isMobileBrowser ? undefined : 4,
      width: Platform.OS !== 'web' || isMobileBrowser ? '100%' : undefined,
    },
    quickActionsColumn: {
      flex: Platform.OS !== 'web' || isMobileBrowser ? undefined : 2,
      width: Platform.OS !== 'web' || isMobileBrowser ? '100%' : undefined,
      alignSelf: 'stretch',
    },
    lastSectionWeb: {
      marginBottom: 0, // No bottom margin for last section on web
    },
    sectionHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.md,
      paddingHorizontal: spacing.md,
    },
    sectionTitle: {
      fontSize: typography.fontSize.base,
      color: colors.textDark,
      fontFamily: typography.fontFamily.bold,
      flex: 1,
    },
    callHistoryPaneCallButton: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: colors.primary as string,
      alignItems: 'center',
      justifyContent: 'center',
    },
    refreshButton: {
      padding: spacing.xs,
      marginLeft: spacing.sm,
    },
    refreshIconSpinning: {
      opacity: 0.6,
    },
    platformsList: {
      paddingHorizontal: spacing.xs,
      paddingBottom: spacing.sm, // Add bottom padding to prevent cutoff
    },
    emptyContainer: {
      padding: 20,
      alignItems: 'center',
    },
    emptyText: {
      fontSize: typography.fontSize.base,
      color: colors.text.secondary,
      marginBottom: 4,
    },
    quickActionsList: {
      paddingHorizontal: Platform.OS === 'web' ? spacing.md : spacing.md, // More padding on mobile
      paddingBottom: spacing.sm, // Add bottom padding to prevent cutoff
      gap: isMobileBrowser ? 8 : (Platform.OS === 'web' ? spacing.xs : spacing.sm), // More gap on mobile (12px instead of 6px)
    },
    quickActionCard: {
      backgroundColor: colors.background.surface,
      borderRadius: spacing.borderRadius.lg,
      padding: isMobileBrowser ? spacing.md : (Platform.OS === 'web' ? spacing.lg : spacing.md), // Mobile browser uses mobile app padding
      alignItems: 'center',
      justifyContent: 'center',
      width: getCardWidth('action', screenWidth, isMobileBrowser),
      minHeight: isMobileBrowser ? 95 : (Platform.OS === 'web' ? 100 : 95), // Mobile browser uses mobile app height
      shadowColor: colors.textDark,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    actionIcon: {
      marginBottom: spacing.sm, // Consistent spacing between icon and text
    },
    actionTitle: {
      fontSize: typography.fontSize.sm,
      color: colors.textDark,
      textAlign: 'center',
      fontFamily: typography.fontFamily.medium,
      marginBottom: 0,
      paddingHorizontal: isMobileBrowser ? spacing.xs : (Platform.OS === 'web' ? 0 : spacing.xs), // Mobile browser uses mobile app padding
      lineHeight: isMobileBrowser ? 14 : (Platform.OS === 'web' ? 16 : 14), // Mobile browser uses mobile app line height
      // Ensure text doesn't get cut off on mobile
      flexWrap: 'wrap',
    },
    campaignsList: {
      paddingHorizontal: Platform.OS === 'web' ? spacing.md : 0, // No horizontal padding on mobile for full-width cards
      paddingBottom: spacing.sm, // Add bottom padding to prevent cutoff
      gap: Platform.OS === 'web' ? spacing.md : spacing.md, // Add gap between items
    },
    campaignCard: {
      backgroundColor: colors.background.surface,
      borderRadius: spacing.borderRadius.lg,
      padding: spacing.lg,
      width: getCardWidth('campaign', screenWidth, isMobileBrowser),
      marginHorizontal: Platform.OS !== 'web' ? spacing.md : 0, // Add horizontal margin on mobile for proper spacing between cards
      shadowColor: colors.textDark,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    campaignHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: spacing.sm,
    },
    campaignInfo: {
      flex: 1,
    },
    campaignName: {
      fontSize: typography.fontSize.base,
      color: colors.textDark,
      fontFamily: typography.fontFamily.medium,
      marginBottom: spacing.xs,
    },
    platformTag: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.background.primary,
      paddingHorizontal: spacing.sm,
      paddingVertical: spacing.xs,
      borderRadius: spacing.borderRadius.sm,
    },
    platformText: {
      fontSize: typography.fontSize.xs,
      color: colors.textLight,
      marginLeft: spacing.xs,
      fontFamily: typography.fontFamily.primary,
    },
    statusBadge: {
      paddingHorizontal: spacing.sm,
      paddingVertical: spacing.xs,
      borderRadius: spacing.borderRadius.sm,
    },
    statusText: {
      fontSize: typography.fontSize.xs,
      fontFamily: typography.fontFamily.bold,
    },
    progressContainer: {
      marginBottom: spacing.sm,
    },
    progressBar: {
      height: 6,
      backgroundColor: colors.background.primary,
      borderRadius: 3,
      overflow: 'hidden',
      marginBottom: spacing.xs,
    },
    progressFill: {
      height: '100%',
      backgroundColor: colors.primary,
      borderRadius: 3,
    },
    progressText: {
      fontSize: typography.fontSize.xs,
      color: colors.textLight,
      fontFamily: typography.fontFamily.primary,
      textAlign: 'center',
      marginBottom: spacing.xs,
    },
    scheduledInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
    },
    campaignMetrics: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
      marginBottom: spacing.sm,
      gap: spacing.sm / 2,
    },
    metricItem: {
      flex: 1,
      alignItems: 'center',
      gap: 4,
      minWidth: 60,
    },
    metricValue: {
      fontSize: typography.fontSize.sm,
      color: colors.primary,
      fontFamily: typography.fontFamily.bold,
      marginBottom: spacing.xs,
    },
    metricLabel: {
      fontSize: typography.fontSize.xs,
      color: colors.textLight,
      fontFamily: typography.fontFamily.primary,
    },
    activityItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    activityIcon: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.background.primary,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.md,
    },
    activityContent: {
      flex: 1,
    },
    activityTitle: {
      fontSize: typography.fontSize.sm,
      color: colors.textDark,
      marginBottom: spacing.xs,
      fontFamily: typography.fontFamily.medium,
    },
    activityDescription: {
      fontSize: typography.fontSize.xs,
      color: colors.textLight,
      fontFamily: typography.fontFamily.primary,
      marginBottom: spacing.xs,
    },
    activityTime: {
      fontSize: typography.fontSize.xs,
      color: colors.textLight,
      fontFamily: typography.fontFamily.primary,
      marginTop: 2,
    },
    activityList: {
      paddingHorizontal: spacing.md,
    },
    activityStats: {
      alignItems: 'flex-end',
      marginLeft: spacing.sm,
    },
    activityStatValue: {
      fontSize: typography.fontSize.base,
      fontFamily: typography.fontFamily.bold,
      color: colors.textDark,
    },
    activityStatLabel: {
      fontSize: typography.fontSize.xs,
      fontFamily: typography.fontFamily.regular,
      color: colors.textLight,
    },
    activityItemUnread: {
      backgroundColor: colors.background.secondary,
    },
    activityTitleUnread: {
      fontFamily: typography.fontFamily.bold,
    },
    unreadDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: colors.primary,
      marginLeft: spacing.sm,
    },
    quickStatsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      paddingHorizontal: spacing.md,
      gap: spacing.md,
      justifyContent: 'space-between',
    },
    quickStatCard: {
      backgroundColor: colors.background.surface,
      borderRadius: spacing.borderRadius.lg,
      padding: spacing.md,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: Platform.OS === 'web' ? 140 : (screenWidth - (spacing.md * 6)) / 2, // 2 cards per row on mobile
      flex: Platform.OS === 'web' ? 0 : 1,
      maxWidth: Platform.OS === 'web' ? 180 : '48%',
      shadowColor: colors.textDark,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    quickStatValue: {
      fontSize: typography.fontSize.xl,
      fontFamily: typography.fontFamily.bold,
      color: colors.textDark,
      marginTop: spacing.xs,
      marginBottom: spacing.xs / 2,
    },
    quickStatLabel: {
      fontSize: typography.fontSize.xs,
      fontFamily: typography.fontFamily.regular,
      color: colors.textLight,
      textAlign: 'center',
    },
    activityStatus: {
      width: 8,
      height: 8,
      borderRadius: 4,
      marginLeft: spacing.sm,
    },
    statusIndicator: {
      width: 6,
      height: 6,
      borderRadius: 3,
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      marginLeft: spacing.sm,
    },
    platformName: {
      fontSize: typography.fontSize.sm,
      color: colors.textDark,
      fontFamily: typography.fontFamily.medium,
      textAlign: 'center',
      marginBottom: spacing.xs,
    },
    platformGrowth: {
      alignItems: 'center',
    },
    growthText: {
      fontSize: typography.fontSize.xs,
      fontFamily: typography.fontFamily.bold,
      marginBottom: spacing.xs,
    },
    subscriptionBadge: {
      paddingHorizontal: spacing.xs,
      paddingVertical: 2,
      borderRadius: 8,
      marginBottom: spacing.xs,
      alignSelf: 'center',
    },
    subscriptionText: {
      fontSize: typography.fontSize.xs,
      fontFamily: typography.fontFamily.bold,
      textAlign: 'center',
    },
    subscriptionLimits: {
      marginBottom: spacing.xs,
    },
    limitText: {
      fontSize: typography.fontSize.xs,
      color: colors.textLight,
      fontFamily: typography.fontFamily.primary,
      textAlign: 'center',
    },
    // Services Section Styles
    servicesGrid: {
      gap: spacing.md,
    },
    serviceCard: {
      backgroundColor: colors.background.surface,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.sm,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    serviceHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: spacing.md,
    },
    serviceIcon: {
      width: 48,
      height: 48,
      borderRadius: 24,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: spacing.sm,
    },
    serviceInfo: {
      flex: 1,
    },
    serviceName: {
      fontSize: typography.fontSize.lg,
      fontFamily: typography.fontFamily.bold,
      color: colors.textDark,
      marginBottom: 2,
    },
    servicePlan: {
      fontSize: typography.fontSize.sm,
      fontFamily: typography.fontFamily.primary,
      color: colors.textLight,
    },
    serviceStatus: {
      paddingHorizontal: spacing.sm,
      paddingVertical: 4,
      borderRadius: 12,
    },
    serviceStatusText: {
      fontSize: typography.fontSize.xs,
      fontFamily: typography.fontFamily.bold,
    },
    serviceLimits: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: spacing.md,
    },
    limitItem: {
      alignItems: 'center',
      flex: 1,
    },
    limitLabel: {
      fontSize: typography.fontSize.xs,
      color: colors.textLight,
      fontFamily: typography.fontFamily.primary,
      marginBottom: 4,
    },
    limitValue: {
      fontSize: typography.fontSize.base,
      fontFamily: typography.fontFamily.bold,
      color: colors.primary,
    },
    serviceFeatures: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.sm,
    },
    featuresTitle: {
      fontSize: typography.fontSize.sm,
      fontFamily: typography.fontFamily.bold,
      color: colors.textDark,
      marginBottom: spacing.xs,
    },
    featuresList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.xs,
    },
    featureItem: {
      fontSize: typography.fontSize.xs,
      color: colors.textLight,
      fontFamily: typography.fontFamily.primary,
      backgroundColor: colors.background.primary,
      paddingHorizontal: spacing.xs,
      paddingVertical: 2,
      borderRadius: 4,
    },
    campaignFooter: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: spacing.sm,
    },
    campaignDate: {
      fontSize: typography.fontSize.xs,
      color: colors.textLight,
      fontFamily: typography.fontFamily.primary,
    },
    budgetInfo: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    budgetSpent: {
      fontSize: typography.fontSize.xs,
      color: colors.primary,
      fontFamily: typography.fontFamily.bold,
    },
    budgetTotal: {
      fontSize: typography.fontSize.xs,
      color: colors.textLight,
      fontFamily: typography.fontFamily.primary,
      marginLeft: spacing.xs,
    },
    // Campaign Modal Styles
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
      ...(Platform.OS === 'web' && {
        position: 'fixed' as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
      }),
    },
    addContactModalContent: {
      backgroundColor: colors.background.surface,
      borderTopLeftRadius: spacing.borderRadius.xl,
      borderTopRightRadius: spacing.borderRadius.xl,
      maxHeight: '95%',
      minHeight: '80%',
      width: '100%',
      paddingBottom: Platform.OS === 'ios' ? spacing.xl : spacing.md,
      ...(Platform.OS === 'web' && isMobileBrowser && {
        maxWidth: '95%' as any,
        marginLeft: 'auto' as any,
        marginRight: 'auto' as any,
      }),
    },
    addContactModalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    addContactModalTitle: {
      fontSize: typography.fontSize.lg,
      fontFamily: typography.fontFamily.bold,
      color: colors.textDark,
    },
    executionHistorySection: {
      marginTop: spacing.xl,
    },
    executionHistoryHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      marginBottom: spacing.md,
      paddingBottom: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    executionHistoryTitle: {
      fontSize: typography.fontSize.lg,
      fontFamily: typography.fontFamily.semibold,
      color: colors.text.primary,
    },
    executionHistoryList: {
      gap: spacing.md,
    },
    executionLogCard: {
      backgroundColor: colors.background.secondary,
      borderRadius: spacing.borderRadius.md,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    executionLogHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: spacing.sm,
      gap: spacing.xs,
    },
    executionStatusDot: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    executionStatusText: {
      fontSize: typography.fontSize.base,
      fontFamily: typography.fontFamily.semibold,
      color: colors.text.primary,
    },
    retryText: {
      fontSize: typography.fontSize.xs,
      fontFamily: typography.fontFamily.regular,
      color: colors.text.secondary,
    },
    executionLogDetails: {
      gap: spacing.xs,
    },
    executionLogRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    executionLogLabel: {
      fontSize: typography.fontSize.sm,
      fontFamily: typography.fontFamily.medium,
      color: colors.text.secondary,
      flex: 1,
    },
    executionLogValue: {
      fontSize: typography.fontSize.sm,
      fontFamily: typography.fontFamily.regular,
      color: colors.text.primary,
      flex: 2,
      textAlign: 'right',
    },
    errorContainer: {
      marginTop: spacing.sm,
      padding: spacing.sm,
      backgroundColor: `${String(colors.error || '#EF4444')}10`,
      borderRadius: spacing.borderRadius.sm,
      borderLeftWidth: 3,
      borderLeftColor: colors.error || '#EF4444',
    },
    errorLabel: {
      fontSize: typography.fontSize.sm,
      fontFamily: typography.fontFamily.semibold,
      color: colors.error || '#EF4444',
      marginBottom: spacing.xs,
    },
    errorText: {
      fontSize: typography.fontSize.sm,
      fontFamily: typography.fontFamily.regular,
      color: colors.text.primary,
    },

  });

  // Handle service completion navigation
  const handleCompleteService = (service: ServiceStatus) => {
    console.log('🔍 [HomeScreen] handleCompleteService called with service:', {
      name: service.name,
      code: service.code,
      status: service.status
    });
    console.log('🔍 [HomeScreen] navigateTo function available:', !!navigateTo);
    console.log('🔍 [HomeScreen] typeof navigateTo:', typeof navigateTo);

    switch (service.code.toLowerCase()) {
      case 'wabu':
      case 'wapi':
      case 'whatsapp':
        console.log('✅ [HomeScreen] Navigating to whatsapp-business-setup');
        console.log('🔍 [HomeScreen] About to call navigateTo...');
        try {
          navigateTo('whatsapp-business-setup');
          console.log('✅ [HomeScreen] navigateTo call completed successfully');
        } catch (error) {
          console.error('❌ [HomeScreen] Error calling navigateTo:', error);
        }
        break;
      case 'fbm':
      case 'facebook':
      case 'facebook-messenger':
        console.log('✅ [HomeScreen] Navigating to facebook-messenger-setup');
        console.log('🔍 [HomeScreen] About to call navigateTo...');
        try {
          navigateTo('facebook-messenger-setup');
          console.log('✅ [HomeScreen] navigateTo call completed successfully');
        } catch (error) {
          console.error('❌ [HomeScreen] Error calling navigateTo:', error);
        }
        break;
      case 'x_twitter':
      case 'x-twitter':
      case 'twitter':
      case 'xtwitter':
        console.log('✅ [HomeScreen] Navigating to x-twitter-setup');
        try {
          navigateTo('x-twitter-setup');
          console.log('✅ [HomeScreen] navigateTo call completed successfully');
        } catch (error) {
          console.error('❌ [HomeScreen] Error calling navigateTo:', error);
        }
        break;
      case 'phone':
        console.log('✅ [HomeScreen] Navigating to phone-setup');
        try {
          navigateTo('phone-setup');
          console.log('✅ [HomeScreen] navigateTo call completed successfully');
        } catch (error) {
          console.error('❌ [HomeScreen] Error calling navigateTo:', error);
        }
        break;
      case 'ai':
        navigateTo('ai-setup');
        break;
      case 'visits':
      case 'visit':
        console.log('✅ [HomeScreen] Navigating to visits-setup');
        try {
          navigateTo('visits-setup');
          console.log('✅ [HomeScreen] navigateTo call completed successfully');
        } catch (error) {
          console.error('❌ [HomeScreen] Error calling navigateTo:', error);
        }
        break;
      case 'user':
      case 'usrs':
      case 'users':
        // Navigate to user management setup
        console.log('ℹ️ [HomeScreen] Navigating to user management setup');
        navigateTo('user-setup');
        break;
      case 'identity_email':
        console.log('✅ [HomeScreen] Navigating to add-verify-identity (email)');
        navigateTo('add-verify-identity', undefined, { identityType: 'email' });
        break;
      case 'identity_mobile':
        console.log('✅ [HomeScreen] Navigating to add-verify-identity (mobile)');
        navigateTo('add-verify-identity', undefined, { identityType: 'mobile' });
        break;
      default:
        console.log('ℹ️ [HomeScreen] Service setup for:', service.name);
        showNotification('info', 'Service Setup', `${service.name} setup is being configured. Please contact support for assistance.`);
    }
  };

  // Set the active tab to 'home' when this screen loads
  useEffect(() => {
    setActiveTab('home');
  }, [setActiveTab]);

  const loadDashboardData = async () => {
    try {
      console.log('🔍 [HomeScreen] Loading dashboard data...');

      // Get omni-channel dashboard data
      const dashboardResult = await apiService.getOmniChannelDashboard();
      console.log('🔍 [HomeScreen] Dashboard result:', dashboardResult);

      if (dashboardResult.statusCode === 1000 && dashboardResult.data) {
        setDashboardData(dashboardResult.data);
      }

      // Get channels status
      const channelsResult = await apiService.getChannelsStatus();
      console.log('🔍 [HomeScreen] Channels result:', channelsResult);

      if (channelsResult.statusCode === 1000 && channelsResult.data) {
        setChannelsData(channelsResult.data);
      }

    } catch (error) {
      console.error('🔍 [HomeScreen] Error loading dashboard data:', error);
      // Don't show error notification for dashboard data - it's optional
    }
  };

  // Load dashboard data once a token is actually available. Depending on tokens?.token
  // (not just an empty-deps mount effect) means that if HomeScreen renders in the brief
  // window right after business selection where the store hasn't yet reflected the new
  // token, this re-runs the moment it does, instead of failing once with "Authorization
  // header is required" and never retrying.
  useEffect(() => {
    if (!tokens?.token) {
      console.log('⏳ [HomeScreen] No token yet, deferring dashboard data load...');
      return;
    }
    console.log('🔍 [HomeScreen] Token available, loading dashboard data...');
    loadCurrentSubscription(); // Load subscription data first
    loadDashboardData();
  }, [tokens?.token]);


  // Latest published posts across the connected Facebook pages.
  // facebookPages resolves after mount, hence the effect.
  const loadRecentPosts = async () => {
    if (facebookPages.length === 0) {
      setRecentPosts([]);
      return;
    }
    try {
      const res = await apiService.listFacebookPosts({ status: 'published', limit: 3 });
      setRecentPosts(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRecentPosts([]);
    }
  };
  useEffect(() => {
    loadRecentPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facebookPages.length]);

  const onRefresh = async () => {
    setRefreshing(true);
    setHomeRefreshToken((n) => n + 1);
    await Promise.all([
      refreshProfile(),
      loadDashboardData(),
      loadRecentPosts(),
    ]);
    setRefreshing(false);
  };



  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      logout();
    }
  };

  // Handle contact added
  const handleContactAdded = (contact: any) => {
    console.log('✅ [HomeScreen] Contact added:', contact);
    setShowAddContactModal(false);
    if (Platform.OS === 'web') {
      setRightPaneContent('empty');
    }
    showNotification('success', 'Contact Added', `${contact.name} has been added successfully`);
  };

  // Handle conversation started
  const handleConversationStarted = (contact: { name: string; phoneNumber: string; topic: string }) => {
    console.log('✅ [HomeScreen] Conversation started:', contact);
    setShowAddContactModal(false);
    if (Platform.OS === 'web') {
      setRightPaneContent('empty');
    }
    showNotification('success', 'Conversation Started', `Conversation with ${contact.name} has been initiated`);
    navigateAwayFromHome('chat');
  };

  const handleTabNavigation = createTabPressHandler(navigateTo, setActiveTab, { onMore: () => setShowMoreModal(true) });
  const handleTabPress = (screenId: string) => {
    closeViewedContact();
    handleTabNavigation(screenId);
  };

  const handleQuickAction = (actionId: string) => {
    if (actionId === 'whatsapp-message') {
      navigateAwayFromHome('send-whatsapp-message');
    } else if (actionId === 'add-contact') {
      if (!hasPermissionAtLeast('contacts.manage', 'edit')) return;
      if (Platform.OS === 'web') {
        // On web, open in right pane
        setRightPaneContent('add-contact');
        setShowRightPane(true);
      } else {
        // On mobile, show modal
        setShowAddContactModal(true);
      }
    } else if (actionId === 'contacts') {
      navigateAwayFromHome('contacts');
    } else if (actionId === 'chat') {
      navigateAwayFromHome('chat'); // This now navigates to the unified InboxScreen
    } else if (actionId === 'products') {
      navigateAwayFromHome('products');
    } else if (actionId === 'catalogues') {
      navigateAwayFromHome('catalogues');
    } else if (actionId === 'quick-replies') {
      navigateAwayFromHome('quick-replies');
    } else if (actionId === 'orders') {
      navigateAwayFromHome('orders');
    } else if (actionId === 'vendors') {
      navigateAwayFromHome('vendors');
    } else if (actionId === 'quotations') {
      navigateAwayFromHome('quotations');
    } else if (actionId === 'calendars') {
      navigateAwayFromHome('business-calendar');
    } else if (actionId === 'visits') {
      navigateAwayFromHome('visits');
    } else if (actionId === 'subscription') {
      navigateAwayFromHome('subscription');
    } else if (actionId === 'posts') {
      navigateAwayFromHome('posts-list');
    } else if (actionId === 'flows-list') {
      navigateAwayFromHome('flows-list');
    } else if (actionId === 'data-lists') {
      navigateAwayFromHome('data-lists');
    } else if (actionId === 'lead-finder') {
      navigateAwayFromHome('lead-finder');
    } else if (actionId === 'template-management') {
      navigateAwayFromHome('template-management');
    } else if (actionId === 'reports') {
      // 'performance' is the existing route id ReportsScreen now renders at
      // (repurposed from the old mock PerformanceScreen — see AppNavigator.tsx).
      navigateAwayFromHome('performance');
    } else {
      showNotification('info', 'Feature', `${actionId} feature is being configured. Please contact support for assistance.`);
    }
  };

  // Use latestCampaigns from API, fallback to empty array
  const activeCampaigns = latestCampaigns.length > 0 ? latestCampaigns : [];

  const quickActions = [
    { id: 'flows-list', title: t('quickActions.workflows.title'), description: t('quickActions.workflows.description'), icon: 'git-branch-outline', color: '#10B981' },
    { id: 'quick-replies', title: t('quickActions.quickReplies.title'), description: t('quickActions.quickReplies.description'), icon: 'chatbubble-ellipses-outline', color: '#F59E0B' },
    { id: 'template-management', title: t('quickActions.templateMessage.title'), description: t('quickActions.templateMessage.description'), icon: 'document-text-outline', color: '#3B82F6' },
    { id: 'whatsapp-message', title: t('quickActions.whatsappCampaign.title'), description: t('quickActions.whatsappCampaign.description'), icon: 'send', color: '#25D366' },
    { id: 'calendars', title: t('quickActions.calendarSetup.title'), description: t('quickActions.calendarSetup.description'), icon: 'calendar-outline', color: '#EC4899' },
    { id: 'visits', title: t('quickActions.visits.title'), description: t('quickActions.visits.description'), icon: 'walk-outline', color: '#14B8A6' },
    { id: 'products', title: t('quickActions.products.title'), description: t('quickActions.products.description'), icon: 'cube-outline', color: '#8B5CF6' },
    { id: 'data-lists', title: t('dataLists.title'), description: t('dataLists.empty.message'), icon: 'server-outline', color: '#0D9488' },
    { id: 'orders', title: t('quickActions.orders.title'), description: t('quickActions.orders.description'), icon: 'receipt-outline', color: '#6366F1' },
    { id: 'lead-finder', title: t('leadFinder.title'), description: t('leadFinder.shortcut'), icon: 'ai-bot', color: '#0EA5E9' },
    { id: 'vendors', title: t('quickActions.vendors.title'), description: t('quickActions.vendors.description'), icon: 'business-outline', color: '#059669' },
    { id: 'quotations', title: t('quickActions.quotations.title'), description: t('quickActions.quotations.description'), icon: 'document-text-outline', color: '#DB2777' },
    { id: 'reports', title: t('quickActions.reports.title'), description: t('quickActions.reports.description'), icon: 'bar-chart-outline', color: '#6B7280' },
    ...(facebookPages.length > 0
      ? [{ id: 'posts', title: t('home.posts.title'), description: t('home.posts.shortcut'), icon: 'newspaper-outline', color: '#1877F2' }]
      : []),
  ];


  const renderCampaignCard = ({ item }: { item: any }) => (
    <TouchableOpacity 
      style={styles.campaignCard} 
      onPress={() => handleCampaignPress(item)}
      activeOpacity={0.7}
    >
      <View style={styles.campaignHeader}>
        <View style={styles.campaignInfo}>
          <Text style={styles.campaignName}>{item.name}</Text>
          <View style={styles.platformTag}>
            <Ionicons
              name={
                item.platform?.toLowerCase().includes('whatsapp') ? 'logo-whatsapp' :
                item.platform?.toLowerCase().includes('facebook') || item.platform?.toLowerCase().includes('fbm') || item.platform?.toLowerCase().includes('messenger') ? 'logo-facebook' :
                item.platform?.toLowerCase().includes('telegram') ? 'paper-plane' :
                item.platform?.toLowerCase().includes('sms') ? 'chatbubble' :
                'logo-instagram'
              }
              size={16}
              color={
                item.platform?.toLowerCase().includes('whatsapp') ? '#25D366' :
                item.platform?.toLowerCase().includes('facebook') || item.platform?.toLowerCase().includes('fbm') || item.platform?.toLowerCase().includes('messenger') ? '#0084FF' :
                item.platform?.toLowerCase().includes('telegram') ? '#0088CC' :
                item.platform?.toLowerCase().includes('sms') ? '#6B7280' :
                '#E4405F'
              }
            />
            <Text style={styles.platformText}>{item.platform}</Text>
          </View>
        </View>
        <View style={[styles.statusBadge, {
          backgroundColor: 
            item.status === 'Active' || item.status === 'Completed' ? '#10B981' + '20' :
            item.status === 'Scheduled' || item.status === 'Running' || item.status === 'Partial' ? '#F59E0B' + '20' :
            item.status === 'Failed' ? '#EF4444' + '20' :
            item.status === 'Pending' ? '#6B7280' + '20' :
            '#6B7280' + '20'
        }]}>
          <Text style={[styles.statusText, {
            color: 
              item.status === 'Active' || item.status === 'Completed' ? '#10B981' :
              item.status === 'Scheduled' || item.status === 'Running' || item.status === 'Partial' ? '#F59E0B' :
              item.status === 'Failed' ? '#EF4444' :
              item.status === 'Pending' ? '#6B7280' :
              '#6B7280'
          }]}>{item.status}</Text>
        </View>
      </View>

      <View style={styles.progressContainer}>
        <View style={styles.progressBar}>
          <View style={[
            styles.progressFill, 
            { 
              // For scheduled/pending, show full width to indicate status
              width: item.status === 'Scheduled' || item.status === 'Pending' 
                ? '100%' 
                : `${Math.max(item.progress || 0, 0)}%`,
              backgroundColor: 
                item.status === 'Completed' ? '#10B981' :
                item.status === 'Scheduled' ? '#F59E0B' :
                item.status === 'Failed' ? '#EF4444' :
                item.status === 'Running' ? '#3B82F6' :
                item.status === 'Partial' ? '#F59E0B' :
                item.status === 'Pending' ? '#6B7280' :
                colors.primary
            }
          ]} />
        </View>
        <Text style={styles.progressText}>
          {item.status === 'Scheduled' ? 'Scheduled' :
           item.status === 'Pending' ? 'Pending' :
           item.status === 'Failed' ? 'Failed' :
           item.status === 'Running' ? 'Running' :
           `${item.progress || 0}% Complete`}
        </Text>
      </View>

      <View style={styles.campaignMetrics}>
        <View style={styles.metricItem}>
          <Ionicons name="send" size={14} color={colors.primary} style={{ marginBottom: 2 }} />
          <Text style={styles.metricValue}>{item.sent.toLocaleString()}</Text>
          <Text style={styles.metricLabel}>Sent</Text>
        </View>
        <View style={styles.metricItem}>
          <Ionicons name="checkmark-circle" size={14} color="#10B981" style={{ marginBottom: 2 }} />
          <Text style={styles.metricValue}>{item.delivered.toLocaleString()}</Text>
          <Text style={styles.metricLabel}>Delivered</Text>
        </View>
        <View style={styles.metricItem}>
          <Ionicons 
            name={item.read > 0 ? "eye" : "eye-outline"} 
            size={14} 
            color={item.read > 0 ? (colors.success || '#10B981') : (colors.text.secondary || '#9CA3AF')} 
            style={{ marginBottom: 2 }} 
          />
          <Text style={styles.metricValue}>{(item.read || 0).toLocaleString()}</Text>
          <Text style={styles.metricLabel}>Read</Text>
        </View>
        <View style={styles.metricItem}>
          <Ionicons name="chatbubble-ellipses" size={14} color={colors.info || '#3B82F6'} style={{ marginBottom: 2 }} />
          <Text style={styles.metricValue}>{(item.contactReplied || 0).toLocaleString()}</Text>
          <Text style={styles.metricLabel}>Contact Replied</Text>
        </View>
        <View style={styles.metricItem}>
          <Ionicons name="people-circle" size={14} color={colors.success || '#10B981'} style={{ marginBottom: 2 }} />
          <Text style={styles.metricValue}>{(item.handledConversations || 0).toLocaleString()}</Text>
          <Text style={styles.metricLabel}>Handled</Text>
        </View>
      </View>

      <View style={styles.campaignFooter}>
        {item.status === 'Scheduled' && item.nextExecutionAt ? (
          <View style={styles.scheduledInfo}>
            <Ionicons name="time-outline" size={14} color={colors.text.secondary} />
            <Text style={styles.campaignDate}>
              Scheduled: {new Date(item.nextExecutionAt).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Text>
          </View>
        ) : (
          <Text style={styles.campaignDate}>
            {item.status === 'Completed' || item.status === 'Failed' ? 'Completed' :
             item.status === 'Pending' ? 'Created' : 'Ends'}: {item.endDate}
          </Text>
        )}
        <View style={styles.budgetInfo}>
          {item.status === 'Scheduled' || item.status === 'Pending' ? (
            <>
              <Text style={styles.budgetSpent}>{item.totalContacts || 0}</Text>
              <Text style={styles.budgetTotal}> contacts</Text>
            </>
          ) : (
            <>
              <Text style={styles.budgetSpent}>{item.successful || 0}</Text>
              <Text style={styles.budgetTotal}>/ {item.totalContacts || 0} sent</Text>
            </>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderActivityItem = ({ item }: { item: any }) => (
    <View style={styles.activityItem}>
      <View style={[styles.activityIcon, { backgroundColor: item.color + '20' }]}>
        <Ionicons name={item.icon as any} size={20} color={item.color} />
      </View>
      <View style={styles.activityContent}>
        <Text style={styles.activityTitle}>{item.title}</Text>
        <Text style={styles.activityDescription}>{item.description}</Text>
        <Text style={styles.activityTime}>{item.time}</Text>
      </View>
      <View style={[styles.activityStatus, { backgroundColor: item.status === 'success' ? '#10B981' + '20' : '#6366F1' + '20' }]}>
        <View style={[styles.statusIndicator, { backgroundColor: item.status === 'success' ? '#10B981' : '#6366F1' }]} />
      </View>
    </View>
  );

  const header = (
    <Header
      showNotifications={true}
      showMessages={true}
      onNotificationPress={() => navigateAwayFromHome('notifications')}
      onMessagePress={() => navigateAwayFromHome('chat')}
      // Web navigation props — desktop-web only; mobile-web uses BottomTabs, matching native
      showNavigation={Platform.OS === 'web' && !isMobileBrowser}
      activeTab={activeTab}
      onTabPress={handleTabPress}
    />
  );

  const rightPaneCustomContent = rightPaneContent === 'add-contact' ? (
    <View style={{ flex: 1, padding: 20 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Text style={{ fontSize: 20, fontWeight: 'bold', color: colors.textDark }}>Add Contact</Text>
        <TouchableOpacity onPress={() => {
          setRightPaneContent('empty');
        }}>
          <Ionicons name="close" size={24} color={colors.textDark} />
        </TouchableOpacity>
      </View>
      <ContactEditForm
        contact={null}
        onSave={handleContactAdded}
        onClose={() => {
          setRightPaneContent('empty');
        }}
      />
    </View>
  ) : rightPaneContent === 'view-conversation' || rightPaneContent === 'view-call-history' ? (
    <View style={{ flex: 1, height: '100%' }}>
      <PaneHeader
        titleContent={
          <ConversationIdentifierDropdown
            options={conversationIdentifierOptions}
            selectedChannel={conversationSelectedChannel}
            selectedValue={conversationSelectedIdentifierValue}
            formatChannelName={(channel) => getChannelName(channel, t)}
            onSelect={(option) => {
              switchConversationIdentifier(option);
              setRightPaneContent(option.channel === 'phone' ? 'view-call-history' : 'view-conversation');
            }}
          />
        }
        trailing={
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            {rightPaneContent === 'view-call-history' && !!callHistoryTarget?.phoneNumber && (
              <TouchableOpacity
                style={styles.callHistoryPaneCallButton}
                onPress={handleCallHistoryCall}
                activeOpacity={0.85}
              >
                <Ionicons name="call" size={18} color="#FFFFFF" />
              </TouchableOpacity>
            )}
            <PaneHeaderIconButton icon="close" onPress={closeViewedContact} />
          </View>
        }
      />
      {rightPaneContent === 'view-call-history' ? (
        <CallHistoryScreen
          embedded
          hideEmbeddedHeader
          contactId={callHistoryTarget?.contactId}
          contactName={callHistoryTarget?.contactName}
          phoneNumber={callHistoryTarget?.phoneNumber}
          phoneIndex={callHistoryTarget?.phoneIndex}
          onClose={closeViewedCallHistory}
        />
      ) : viewedConversationId ? (
        <ConversationScreen
          embedded
          conversationId={viewedConversationId}
          onNavigateBack={closeViewedContact}
          onViewContact={() => {}}
        />
      ) : (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      )}
    </View>
  ) : undefined;

  // Registered with the nearest PersistentPaneShell ancestor on desktop web;
  // a harmless no-op on native/narrow mobile-web, where this screen renders
  // its own <ScreenLayout> directly below instead.
  usePaneShellConfig({
    header,
    showBottomTabs: Platform.OS !== 'web' || isMobileBrowser,
    onTabPress: handleTabPress,
    onContactPress: handleContactRowPress,
    activeContactId: viewedContact?.id,
    onRightPaneClose: () => {
      setShowRightPane(true);
      if (rightPaneContent === 'view-call-history') {
        closeViewedCallHistory();
      } else {
        closeViewedContact();
      }
    },
    rightPaneContent: rightPaneContent === 'empty' ? 'empty' : 'custom',
    rightPaneCustomContent,
    rightPaneTab: rightPaneContent === 'view-call-history' ? 'calls' : rightPaneContent === 'view-conversation' ? 'chat' : 'form',
    // Wider than the default pane width — a conversation needs more room
    // than the add-contact form, same 480px convention PostsListScreen.tsx
    // already uses for its own "needs more room" case.
    rightPaneWidthOverride: rightPaneContent === 'view-conversation' || rightPaneContent === 'view-call-history' ? PANE_WIDE_WIDTH : undefined,
  }, isActive);

  const content = (
    <>
      {/* Clicking a contact in the left pane replaces this screen's own
          dashboard with that contact's profile (desktop web) — mirrors
          ContactsListScreen's existing middle-pane pattern. */}
      {viewedContact ? (
        <View style={{ flex: 1 }}>
          <ContactEditForm
            contact={viewedContact}
            onClose={closeViewedContact}
            compact={true}
            onChannelSelect={(channel, identifierValue, channelContactId) => viewedContact && openContactConversation(viewedContact, channel, identifierValue, channelContactId)}
            onViewCallHistory={(contactId, contactName, phoneNumber, phoneIndex) => {
              openCallHistory(contactId, contactName, phoneNumber, phoneIndex);
              setRightPaneContent('view-call-history');
            }}
          />
        </View>
      ) : (
      <ScrollView
        style={styles.content}
        contentContainerStyle={Platform.OS === 'web' ? styles.contentContainerWeb : undefined}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >

        <HomeDashboard
          refreshToken={homeRefreshToken}
          pagePosts={facebookPages.length > 0
            ? { posts: recentPosts, pageId: facebookPages[0]?.page_id, canCreate: hasPermissionAtLeast('fbm.managePosts', 'edit') }
            : null}
          onNavigate={navigateAwayFromHome}
          shortcuts={quickActions}
          onShortcutPress={handleQuickAction}
          {...(setup.canSetUp
            ? {
                setupPercentage: setupHidden || setup.total === 0 ? undefined : Math.round((setup.doneCount / setup.total) * 100),
                setupDetail: [
                  t('setup.progress', { done: setup.doneCount, total: setup.total }),
                  setup.next ? t('setup.next', { step: t(`setup.step.${setup.next.key}.title` as any) }) : null,
                ].filter(Boolean).join(' · '),
                onOpenSetup: () => navigateAwayFromHome('channel-onboarding-stepper'),
                onDismissSetup: hideSetup,
              }
            : {
                setupPercentage: profileCompletion?.percentage,
                onOpenSetup: () => setShowSetupModal(true),
              })}
        />
      </ScrollView>
      )}


      {/* Setup progress — moved off the dashboard into the banner's sheet. */}
      <Modal
        visible={showSetupModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowSetupModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.addContactModalContent}>
            <View style={styles.addContactModalHeader}>
              <Text style={styles.addContactModalTitle}>{t('home.setup.title')}</Text>
              <TouchableOpacity onPress={() => setShowSetupModal(false)}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            <ScrollView>
              <ProfileCompletionWidget
                data={profileCompletion}
                canConfigure={businessRole === 'admin' || businessRole === 'owner'}
                whatsappPhones={whatsappPhones}
                facebookPages={facebookPages}
                xAccounts={xAccounts}
                phoneAccount={phoneAccount}
                businessStats={businessStats}
                onCompleteProfile={() => { setShowSetupModal(false); navigateTo('subscription'); }}
                onCompleteService={(service) => { setShowSetupModal(false); handleCompleteService(service); }}
                onRefresh={onRefresh}
              />
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Add Contact Modal for Mobile */}
      <Modal
        visible={showAddContactModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowAddContactModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.addContactModalContent}>
            <View style={styles.addContactModalHeader}>
              <Text style={styles.addContactModalTitle}>Add Contact</Text>
              <TouchableOpacity onPress={() => setShowAddContactModal(false)}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            <ContactEditForm
              contact={null}
              onSave={handleContactAdded}
              onClose={() => setShowAddContactModal(false)}
            />
          </View>
        </View>
      </Modal>

      {/* More Options Modal */}
      <MoreOptionsModal
        visible={showMoreModal}
        onClose={() => setShowMoreModal(false)}
      />

      <CampaignDetailsModal
        visible={showCampaignModal}
        campaign={selectedCampaign}
        onClose={() => {
          setShowCampaignModal(false);
          setSelectedCampaign(null);
        }}
        accountForSidePanes={Platform.OS === 'web' && showRightPane}
      />
    </>
  );

  // Desktop web: AppNavigator routes this screen through PersistentPaneShell,
  // which owns the actual <ScreenLayout> — this screen just returns its
  // content for the shell to place. Native/narrow mobile-web: no shell
  // exists, so this screen renders its own <ScreenLayout> exactly as every
  // screen outside the shell group still does.
  if (isDesktopPaneLayout) {
    return content;
  }

  return (
    <ScreenLayout
      header={header}
      onTabPress={handleTabPress}
      showBottomTabs={Platform.OS !== 'web' || isMobileBrowser}
      showLeftPane={showLeftPane}
      onLeftPaneClose={() => {}}
      leftPaneContent="contacts"
      showRightPane={showRightPane}
      onRightPaneClose={() => {
        setShowRightPane(true);
        setRightPaneContent('empty');
      }}
      rightPaneContent={rightPaneContent === 'add-contact' ? 'custom' : 'empty'}
      rightPaneCustomContent={rightPaneCustomContent}
      rightPaneTab="form"
    >
      {content}
    </ScreenLayout>
  );
};
